import { Link as MuiLink } from '@mui/material';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useIntl, defineMessages } from 'react-intl';

import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';

import type { EventType } from '@constants/event_types';
import { EventFriendlyText } from '@constants/event_types';

import { formatDate, formatName } from '@utils/format';

import type { AuditLogModel } from 'generated/types';

const messages = defineMessages({
  userName: { id: 'auditLogTable.userName', defaultMessage: 'User' },
  date: { id: 'auditLogTable.date', defaultMessage: 'Date' },
  eventType: { id: 'auditLogTable.eventType', defaultMessage: 'Event Type' },
  resource: { id: 'auditLogTable.resource', defaultMessage: 'Link' },
  details: { id: 'auditLogTable.details', defaultMessage: 'Details' },
  noLogs: { id: 'auditLogTable.noLogs', defaultMessage: 'No audit logs found' },
  loading: { id: 'auditLogTable.loading', defaultMessage: 'Loading......' },
});

interface AuditLogsTableProps {
  auditLogs: AuditLogModel[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  loading: boolean;
  children?: React.ReactNode;
}

function AuditLogsTable({
  auditLogs,
  total,
  page,
  setPage,
  pageSize,
  setPageSize,
  loading,
  children,
}: AuditLogsTableProps) {
  const { formatMessage } = useIntl();

  const tableData = auditLogs.map((log: AuditLogModel) => ({
    user_name: formatName(
      log.user?.first_name ?? '',
      log.user?.last_name ?? ''
    ),
    event_type: log.event_type,
    event_resource: log.event_resource,
    event_details: log.event_details,
    created_at: log.created_at,
  }));

  return (
    <div>
      {children}
      <MuiDataTable
        data={tableData}
        columns={[
          {
            name: 'user_name',
            label: formatMessage(messages.userName),
          },
          {
            name: 'created_at',
            className: 'date',
            label: formatMessage(messages.date),
            options: {
              sort: true,
              customBodyRender: (val: string) =>
                val ? formatDate({ date: val, formatter: 'datetime' }) : '-',
            },
          },
          {
            name: 'event_type',
            className: 'small',
            label: formatMessage(messages.eventType),
            options: {
              customBodyRender: (val: string) =>
                val ? EventFriendlyText[val as EventType] : '-',
            },
          },
          {
            name: 'event_resource',
            label: formatMessage(messages.resource),
            options: {
              customBodyRender: (val: string) =>
                val ? (
                  <MuiLink href={val} underline="hover" color="primary">
                    View
                  </MuiLink>
                ) : (
                  '-'
                ),
            },
          },
          {
            name: 'event_details',
            className: 'small',
            label: formatMessage(messages.details),
            options: {
              setCellProps: () => ({
                style: {
                  maxWidth: '400px',
                },
              }),
            },
          },
        ]}
        options={{
          search: false,
          filter: false,
          sort: true,
          serverSide: true,
          count: total,
          page: page - 1,
          rowsPerPage: pageSize,
          onChangePage: (newPage) => setPage(newPage + 1),
          onChangeRowsPerPage: (numberOfRows: number) => {
            setPageSize(numberOfRows);
            setPage(1);
          },
          textLabels: {
            body: {
              noMatch: loading
                ? formatMessage(messages.loading)
                : formatMessage(messages.noLogs),
            },
          },
        }}
      />
    </div>
  );
}

export default withErrorBoundary(AuditLogsTable, {
  FallbackComponent: ErrorBoundaryFallback,
});
